import React, { useMemo } from "react";
import { CheatSheetView, useCheatMarks } from "../TierBuilder";
import { loadOverrides, loadTierNames } from "../../data/displayTiers";
import { UNIQUE_PLAYERS } from "../../data/players";
import { poolFor } from "./PlayersLeft";

/**
 * LiveCheatSheet — the BOARD tab. The user's own cheat sheet, crossing itself
 * off as sales are entered.
 *
 * ONE STATE, ONE RENDERER. Rather than syncing a second surface with the Tier
 * Builder's sheet, this renders the SAME component with marks supplied from
 * board.sales. Nothing can drift because there is nothing to keep in step, and
 * printing from the Live Board works for free: the print stylesheet is scoped
 * to `.tier-cheat-sheet`, which CheatSheetView still renders.
 *
 * 🔴 IT RENDERS CheatSheetView, NOT CheatSheet. CheatSheet calls
 * usePlayerCard() for its prices, and PlayerCardContext is createContext(null),
 * so that destructure throws anywhere outside PlayerCardProvider, which is
 * where the /watch spectator route lives. Prices arrive as a prop here.
 *
 * The tier edits are the user's own, read per league from displayTiers, so the
 * sheet on the Live Board is the sheet they built in the Tier Builder.
 */

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 DEF AND K ARE COLUMNS OF THE SHEET, NOT A BLOCK UNDER IT.
 *
 * They were a two-column block below CheatSheetView, and nobody scrolls past
 * the last tier. On the BOARD tab the sheet IS the screen, so a panel beneath
 * it is a panel that does not exist. Six columns across the top is the whole
 * point: the operator's eye already goes there.
 *
 * CheatSheetView takes one optional `extraColumns` prop and renders them in
 * its own grid, with its own head, row, sticker and strike. Everything below
 * is the caller's half: which two positions, and whose list.
 *
 * ⚠️ THE POOL IS PlayersLeft's, DELIBERATELY. `poolFor` is where the 32
 * defenses and the 33 kickers already live (UNIQUE_PLAYERS for DEF; the union
 * of liveBoardKickers.json and players.js for K). One definition, so the BOARD
 * tab and the PLAYERS LEFT tab can never disagree about who exists.
 *
 * ⚠️ ALPHABETICAL, which is what `poolFor` returns for these two. There is no
 * public ADP for either, so any other order would be OUR opinion.
 * ══════════════════════════════════════════════════════════════════════
 */

function formatNoteFor(board) {
  return "Dollar estimates hidden: this board is $" + board.budget + " / "
    + board.teamNames.length + " teams, but prices come from your saved league. "
    + "Order and tiers still hold.";
}

export default function LiveCheatSheet({ board, dynamicPrices, priceSettings }) {
  const league = board.leagueName;
  const markState = useCheatMarks(league);

  // Sold players, keyed exactly as the sheet keys its rows and dynamicPrices
  // keys its entries: `${name}-${pos}`.
  const sold = useMemo(() => {
    const m = new Map();
    for (const s of board.sales) m.set(s.name + "-" + s.pos, s.price);
    return m;
  }, [board.sales]);

  const userTierOverrides = useMemo(() => loadOverrides(league), [league]);
  const overrideKeys = useMemo(() => new Set(Object.keys(userTierOverrides)), [userTierOverrides]);
  const tierNames = useMemo(() => loadTierNames(league), [league]);

  // The sheet's depth follows the LEAGUE BEING DRAFTED, not the app's saved
  // settings: a 12-team board should print twelve teams' worth of players.
  const sheetSettings = useMemo(
    () => ({ positions: board.positions, teams: board.teamNames.length }),
    [board.positions, board.teamNames.length]
  );

  // Same format guard as the tier cliff: dynamicPrices is built from APP
  // settings, so if the board was set up with a different budget or team count
  // every dollar on the sheet is denominated in the wrong league. Ordering and
  // tier structure survive a rescale; absolute dollars do not.
  const budgetDiffers = !!priceSettings && priceSettings.budget != null
    && priceSettings.budget !== board.budget;
  const teamsDiffer = !!priceSettings && priceSettings.teams != null
    && priceSettings.teams !== board.teamNames.length;
  const suppressDollars = budgetDiffers || teamsDiffer;

  // 32 defenses and 33 kickers, both independent of room size. Memoised on the
  // board because that is what `poolFor` reads; the values themselves do not
  // move mid-draft.
  const extraColumns = useMemo(() => [
    { pos: "DEF", rows: poolFor("DEF", board) },
    { pos: "K", rows: poolFor("K", board) },
  ], [board]);

  return (
    <CheatSheetView
      key={league}
      players={UNIQUE_PLAYERS}
      settings={sheetSettings}
      userTierOverrides={userTierOverrides}
      overrideKeys={overrideKeys}
      tierNames={tierNames}
      activeLeagueName={league}
      dynamicPrices={dynamicPrices}
      sold={sold}
      suppressDollars={suppressDollars}
      formatNote={suppressDollars ? formatNoteFor(board) : null}
      extraColumns={extraColumns}
      /**
       * 🔴 THE SHEET IS THE SCREEN HERE. CheatSheetView caps itself at 900px
       * and centres, which is right on the Tier Builder page and wrong on
       * /live: at 2560px the six columns sat in the middle third with names
       * cut to eight characters and two-thirds of the monitor empty.
       *
       * `fluid` drops the cap so the sheet spans its parent, which is the
       * board's own content container — the same width as the WAITING ON
       * panel above it. It adds no width of its own: LiveBoard's `live-page`
       * is the single place that decides how wide the board is, and it still
       * decides for this too.
       */
      fluid
      {...markState}
    />
  );
}
